import { ChangeEvent, useState } from "react";
import DashboardLayout from "../../../../Layout/DashboardLayout";
import { useGetCategoryQuery } from "../../../../hooks/CategoryManagement/useGetCategoryQuery";
import { message, Modal, Table, TablePaginationConfig, Upload } from "antd";
import SearchBox from "./SearchBox";
import Loader from "../../../../Components/Loader";
import Error from "Components/Error";
import Input from "Components/Input";
import Textarea from "Components/Textarea";
import { useUpdateCategoryMutation } from "hooks/CategoryManagement/useUpdateCategoryMutation";
import { queryClient } from "services/queryClient";
import Button from "Components/Button";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { useDeleteCategoryMutation } from "hooks/CategoryManagement/useDeleteCategoryMutation";
import { useIconUploader } from "hooks/UploadIcon";

const initialState = {
  name: "",
  description: "",
  iconLink: "",
};

const ProductCategory = () => {
  const [search, setSearch] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [id, setId] = useState(null);
  const { data, isPending, isError } = useGetCategoryQuery({ search });
  const [payload, setPayload] = useState(initialState);

  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setPayload((prev) => ({ ...prev, [name]: value }));
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: any) => (
        <span>{record?.name ?? "Unknown Product"}</span>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description: string) => (
        <span className="text-[13px]">{description}</span>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt: string) => new Date(createdAt).toLocaleString(),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (updatedAt: string) => new Date(updatedAt).toLocaleString(),
    },
    {
      title: "Total Products",
      dataIndex: "totalProducts",
      key: "totalProducts",
      render: (totalProducts: number) => totalProducts,
    },
    {
      title: "Action",
      key: "action",
      render: (text: string, record: any) => (
        <button
          onClick={() => {
            setPayload({
              name: record?.name,
              description: record?.description,
              iconLink: record?.iconLink,
            });
            setId(record?.id);
            setIsModalVisible(true);
          }}
        >
          Edit
        </button>
      ),
    },
  ];

  const handleCancel = () => {
    setIsModalVisible(false);
    setPayload(initialState);
  };

  const { mutateAsync, isPending: isUpdating } = useUpdateCategoryMutation(id);

  const handleDelete = (branch: any) => {
    setIsDeleteModalOpen(true);
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: data?.data?.meta?.limit,
  });

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current ?? 1,
      pageSize: newPagination.pageSize ?? 10,
    }));
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const { mutateAsync: deleteCatalog, isPending: isDeletingCatalog } =
    useDeleteCategoryMutation();

  const handleSubmit = () => {
    deleteCatalog(rowSelection?.selectedRowKeys, {
      onSuccess: () => {
        setIsDeleteModalOpen(false);
        setSelectedRowKeys([]);
      },
    });
  };

  const { mutateAsync: uploadImage, isPending: isUploadPending } =
    useIconUploader({ payload, setPayload });

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      {isUploadPending ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload Icon</div>
    </button>
  );

  const handleUpload = async (info: any) => {
    const file = info.file.originFileObj;

    if (file) {
      const reader = new FileReader();

      reader.onload = async () => {
        const imageBase64 = reader.result as string;
        // setImages(imageBase64);

        try {
          await uploadImage(imageBase64, {
            onSuccess: (data) => {
              setPayload({ ...payload, iconLink: data?.data?.fileUrl });
            },
          });
        } catch (error) {
          console.error("Image upload failed:", error);
          message.error("Error uploading file");
        }
      };

      reader.readAsDataURL(file);
    }
  };

  return (
    <DashboardLayout
      title="Products Category"
      back={false}
      right={<SearchBox search={search} setSearch={setSearch} />}
    >
      <div className="px-[10px] pr-[40px]">
        {isPending && <Loader />}
        {isError && <Error />}

        {rowSelection?.selectedRowKeys?.length > 0 && (
          <button
            onClick={handleDelete}
            className="flex gap-2 items-center bg-orange p-2 rounded-md text-white text-[12px] font-bold"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="size-4"
            >
              <path
                fillRule="evenodd"
                d="M16.5 4.478v.227a48.816 48.816 0 0 1 3.878.512.75.75 0 1 1-.256 1.478l-.209-.035-1.005 13.07a3 3 0 0 1-2.991 2.77H8.084a3 3 0 0 1-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 0 1-.256-1.478A48.567 48.567 0 0 1 7.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 0 1 3.369 0c1.603.051 2.815 1.387 2.815 2.951Zm-6.136-1.452a51.196 51.196 0 0 1 3.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 0 0-6 0v-.113c0-.794.609-1.428 1.364-1.452Zm-.355 5.945a.75.75 0 1 0-1.5.058l.347 9a.75.75 0 1 0 1.499-.058l-.346-9Zm5.48.058a.75.75 0 1 0-1.498-.058l-.347 9a.75.75 0 0 0 1.5.058l.345-9Z"
                clipRule="evenodd"
              />
            </svg>
            Delete
          </button>
        )}
        {!isPending && data && (
          <Table
            rowKey="id"
            columns={columns}
            pagination={pagination}
            rowSelection={rowSelection}
            onChange={handleTableChange}
            scroll={{ y: "70vh" }}
            dataSource={data?.data?.docs}
            className="pl-[19px] pr-[40px] manrope mt-[20px]"
          />
        )}
        <Modal
          // title="Delete Categories"
          title={`Delete ${rowSelection?.selectedRowKeys.length} branch${
            rowSelection?.selectedRowKeys.length > 1 ? "es" : ""
          }`}
          open={isDeleteModalOpen}
          onCancel={closeDeleteModal}
          footer={null}
        >
          <h3 className="text-[16px] maanrope mb-4">
            Confirm that you'd like to delete the selected item(s)
          </h3>

          <Button
            label="Delete"
            isLoading={isDeletingCatalog}
            className="text-white bg-[#f00] p-[12px] font-bold rounded-md ease-in-out duration-300"
            onClick={() => {
              handleSubmit();
            }}
          />
        </Modal>

        <Modal
          title="Edit Category"
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
        >
          <Input
            type="text"
            name="name"
            label="Name"
            value={payload.name}
            placeholder="Enter Category Name"
            onChange={handleInputChange}
          />
          <Textarea
            label="Description"
            name={"description"}
            value={payload?.description}
            placeholder={""}
            onChange={handleInputChange}
          />
          <label
            htmlFor="iconLink"
            className="block mb-2 text-grey text-[16px] manrope capitalize"
          >
            Upload Icon
          </label>

          <div className="mb-3 bg-white text-[16px] h-[150px] resize-none w-full  ease-in-out duration-300 focus:border-orange rounded-md">
            <Upload
              name="iconLink"
              listType="picture-card"
              className="avatar-uploader"
              showUploadList={false}
              onChange={handleUpload}
            >
              {payload?.iconLink ? (
                <img
                  src={payload?.iconLink}
                  alt="iconLink"
                  style={{ width: "100%" }}
                />
              ) : (
                uploadButton
              )}
            </Upload>
          </div>

          <Button
            className="text-white p-[12px] w-full font-bold rounded-md ease-in-out duration-300 bg-brown"
            disabled={!payload.name || !payload.description}
            isLoading={isUpdating}
            label={"Proceed"}
            onClick={() => {
              mutateAsync(payload, {
                onSuccess: (data: any) => {
                  message.success(data?.message);
                  handleCancel();
                  queryClient.invalidateQueries({
                    queryKey: ["useGetCategory"],
                  });
                },
              });
            }}
          />
        </Modal>
      </div>
    </DashboardLayout>
  );
};

export default ProductCategory;
